import React, { useState } from "react";
import { Image } from "react-bootstrap";
import { getInitials, initialsPaletteColor } from "../utils/avatars";

const ImageCropped = React.forwardRef(
  (
    {
      src,
      alt = "",
      size,
      width = size,
      height = size,
      wrapperClasses = "",
      className = "",
      style = {},
      fallbackToInitials = false,
      ...rest
    },
    ref,
  ) => {
    // Opt-in recovery: when the fetch fails, render an inline initials circle in place of the
    // broken img. No second network request, so a flaky CDN can't leave a broken-image artifact.
    // Stores the failed src itself (not a boolean) so the failed state derives synchronously
    // during render when src changes -- no reset effect, no one-frame stale-fallback flash.
    const [failedSrc, setFailedSrc] = useState(null);
    const hasFailed = Boolean(src) && failedSrc === src;

    // A missing src never fires `error` -- the browser makes no request at all -- so an absent image
    // would otherwise render as an empty box for callers that asked for a recovery. Same branch,
    // same initials.
    const missingSrc = !src;

    const { onError: userOnError, ...restWithoutOnError } = rest;
    const handleError = (event) => {
      if (fallbackToInitials) setFailedSrc(src);
      if (userOnError) userOnError(event);
    };
    const attachError = fallbackToInitials || Boolean(userOnError);

    // max-width/aspect-ratio are a containment safety net, not a resize feature: they're no-ops
    // whenever the parent has room for the full pixel size (every existing caller today), and only
    // kick in for a caller sized larger than a narrow container has space for -- e.g. a 250px
    // headshot inside a ~340px mobile card -- shrinking the box instead of forcing it to overflow.
    // CSS ignores aspect-ratio on a box whose width
    // AND height are both pinned in pixels, so a caller that supplies both gets width + maxWidth +
    // aspectRatio with NO explicit height -- the ratio derives the height, so it keeps following a
    // percentage-capped width down instead of leaving a stretched/squashed box once maxWidth kicks
    // in. A height-only caller (no width) still gets its explicit height, since there's no width to
    // derive a ratio from.
    const wrapperStyle = {
      ...(width && { width: `${width}px`, maxWidth: "100%" }),
      ...(height && !width && { height: `${height}px` }),
      ...(width && height && { aspectRatio: `${width} / ${height}` }),
      ...style,
    };

    const renderInitials = () => (
      <div
        ref={ref}
        className={`d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold ${className}`}
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: initialsPaletteColor(alt),
          fontFamily: "sans-serif, Helvetica",
          fontSize: `${(width || 40) * 0.4}px`,
          lineHeight: 1,
        }}
        aria-label={alt}
        data-testid="image-cropped-initials"
      >
        {getInitials(alt)}
      </div>
    );

    const renderImage = () =>
      React.cloneElement(
        <Image
          ref={ref}
          src={src}
          alt={alt}
          className={`object-cover ${className}`}
          style={{ width: "100%", height: "100%" }}
          onError={attachError ? handleError : undefined}
        />,
        restWithoutOnError,
      );

    const failedOrMissing = hasFailed || missingSrc;

    return (
      <div className={`d-inline-block ${wrapperClasses}`}>
        <div className="d-inline-block" style={wrapperStyle}>
          {fallbackToInitials && failedOrMissing ? renderInitials() : renderImage()}
        </div>
      </div>
    );
  },
);

ImageCropped.displayName = "ImageCropped";

export default ImageCropped;
